/**
 * App Component
 *
 * Root component matching original HTML structure.
 */

import { useEffect } from 'preact/hooks';
import { Header } from './components/layout/Header';
import { GroupSelector } from './components/layout/GroupSelector';
import { Player } from './components/player/Player';
import { ToastContainer } from './components/ui/ToastContainer';
import { ModalContainer } from './components/ui/ModalContainer';
import { LoginPromptModal } from './components/auth/LoginPromptModal';
import { DiscoverTab } from './components/songs/DiscoverTab';
import { SubmitTab } from './components/songs/SubmitTab';
import { LeaderboardTab } from './components/leaderboard/LeaderboardTab';
import { ArchivesTab } from './components/archives/ArchivesTab';
import { PodsTab } from './components/groups/PodsTab';

import {
  isAuthenticated,
  isLoading as authLoading,
  initAuth,
  providerConnections,
} from './stores/auth';
import {
  fetchGroups,
} from './stores/groups';
import {
  isAppLoading,
  activeTab,
} from './stores/ui';
import {
  initSpotifySDK,
  createSpotifyPlayer,
  initAppleMusicKit,
} from './stores/player';
import { api } from './api/client';

// Import websocket store to initialize auto-connect effects
import './stores/websocket';

import './styles/index.css';

export function App() {
  // Initialize auth on mount
  useEffect(() => {
    initAuth();
  }, []);

  // Fetch groups when authenticated
  useEffect(() => {
    if (isAuthenticated.value) {
      fetchGroups();
    }
  }, [isAuthenticated.value]);

  // Initialize music providers when authenticated
  useEffect(() => {
    if (!isAuthenticated.value) return;

    const initMusicProviders = async () => {
      const connections = providerConnections.value;

      // Initialize Spotify SDK if connected
      if (connections.get('spotify')?.connected) {
        try {
          await initSpotifySDK();
          await createSpotifyPlayer();
        } catch (e) {
          console.error('Failed to init Spotify:', e);
        }
      }

      // Initialize Apple MusicKit if connected
      if (connections.get('apple_music')?.connected) {
        try {
          const response = await api.get<{ developerToken: string }>('/api/music/auth/apple_music/developer-token');
          if (response.data?.developerToken) {
            await initAppleMusicKit(response.data.developerToken);
          }
        } catch (e) {
          console.error('Failed to init Apple Music:', e);
        }
      }
    };

    initMusicProviders();
  }, [isAuthenticated.value, providerConnections.value]);

  // Show loading state
  if (isAppLoading.value || authLoading.value) {
    return (
      <div class="app-loading-screen">
        <div class="loading-spinner" />
        <p>Loading Sherit...</p>
      </div>
    );
  }

  return (
    <>
      {/* Header with logo, nav, user menu */}
      <Header />

      {/* Group/Pod selector bar */}
      <GroupSelector />

      {/* Main content area - matches original structure */}
      <main>
        {/* DISCOVER TAB */}
        <div
          id="discover"
          class={`tab-content ${activeTab.value === 'discover' ? 'active' : 'hidden'}`}
        >
          <DiscoverTab />
        </div>

        {/* SUBMIT TAB */}
        <div
          id="submit"
          class={`tab-content ${activeTab.value === 'submit' ? 'active' : 'hidden'}`}
        >
          <SubmitTab />
        </div>

        {/* LEADERBOARD TAB */}
        <div
          id="leaderboard"
          class={`tab-content ${activeTab.value === 'leaderboard' ? 'active' : 'hidden'}`}
        >
          <LeaderboardTab />
        </div>

        {/* ARCHIVES TAB */}
        <div
          id="archives"
          class={`tab-content ${activeTab.value === 'archives' ? 'active' : 'hidden'}`}
        >
          <ArchivesTab />
        </div>

        {/* PODS TAB */}
        <div
          id="pods"
          class={`tab-content ${activeTab.value === 'pods' ? 'active' : 'hidden'}`}
        >
          <PodsTab />
        </div>
      </main>

      {/* Sticky player footer */}
      <Player />

      {/* Toast notifications */}
      <ToastContainer />

      {/* Modal container */}
      <ModalContainer />

      {/* Login prompt for unauthenticated users */}
      <LoginPromptModal />
    </>
  );
}
